import { useState } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material';

import { filesAPIService } from '~/api/files';
import ImageIcon from '~/assets/icons/svgs/image.svg?react';
import UploadIcon from '~/assets/icons/svgs/upload.svg?react';
import { Text } from '~/components/ui/text';
import { COLORS } from '~/styles/colors';
import { fileSizeInKb, formatBytes } from '~/utils/common';

type CustomDropZoneProps = {
  onDrop?: (files: File[]) => void;
  maxSize?: number;
  fileTypes: string;
  existedLength: any;
  maxSizeInKb: any;
  acceptTypes: any;
};
export function CustomDropZone({
  onDrop,
  maxSize,
  fileTypes,
  existedLength,
  maxSizeInKb,
  acceptTypes,
}: CustomDropZoneProps) {
  const { t } = useTranslation();

  const [failedFiles, setFailedFiles] = useState([]);
  const [isFileProcessing, setIsFileProcessing] = useState(false);

  const isPDF = acceptTypes === 'application/pdf';

  const onDropHandler = async (values) => {
    setFailedFiles([]);
    setIsFileProcessing(true);

    let files = values.map((item, index) => {
      return {
        ...item,
        previewLink: item.path.endsWith('.pdf') ? '' : URL.createObjectURL(item),
        previewPdfLink: URL.createObjectURL(item),
        size: formatBytes(item.size),
        file: values,
        name: item.name,
        id: `UNIQUE_ID_${index + existedLength + 1}`,
      };
    });

    values.forEach((item, index) => {
      if (fileSizeInKb(item.size) >= maxSizeInKb) {
        setFailedFiles([...failedFiles, item?.name]);

        files = files.filter((item, fileIndex) => index !== fileIndex);
      }
    });

    if (values?.length === 0) {
      setIsFileProcessing(false);

      return;
    }

    const uploadPromises = files.map(async (item, index) => {
      const storageResponse = await filesAPIService.uploadImage(values[index]);

      files[index] = { ...files[index], imageStorageId: storageResponse.data };
    });

    await Promise.all(uploadPromises);

    onDrop(files);
    setIsFileProcessing(false);
  };

  return (
    <Dropzone onDrop={(values) => onDropHandler(values)} accept={{ [acceptTypes]: [] }}>
      {({ getRootProps, getInputProps }) => (
        <StyledDropzoneContent>
          <Box {...getRootProps()}>
            <input {...getInputProps()} accept={acceptTypes} />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              sx={{
                minHeight: isPDF ? '120px' : '172px',
                gap: '8px',
                padding: '12px 16px',
              }}
            >
              {isPDF ? (
                <>
                  <UploadIcon />
                  <Text variant="buttonLinedMed" sx={{ color: COLORS.primary, textDecoration: 'underline' }}>
                    {t('common:uploadPDF')}
                  </Text>
                  <Text variant="inputSmall">
                    {t('common:maxSize')}: {maxSize}MB
                  </Text>
                </>
              ) : (
                <>
                  <StyledImageIcon />
                  <Text variant="buttonLinedMed">
                    <StyledClickText>{t('labels:dragAndDrop1')}</StyledClickText>
                    {t('labels:dragAndDrop2')}
                  </Text>
                  <Text variant="inputSmall">{`${fileTypes} (${t('labels:max')}. ${maxSize}MB)`}</Text>
                </>
              )}

              {!!failedFiles.length && (
                <Text variant="inputSmall" sx={{ color: COLORS.darkRed }}>
                  {t('common:uploadFileFailed')} {failedFiles?.join(', ')}
                </Text>
              )}

              {isFileProcessing && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <CircularProgress size={25} />
                  <Text variant="inputSmall">{t('other:processing')}</Text>
                </Box>
              )}
            </Box>
          </Box>
        </StyledDropzoneContent>
      )}
    </Dropzone>
  );
}

const StyledDropzoneContent = styled('div')(() => ({
  border: `2px dashed ${COLORS.primary}`,
  cursor: 'pointer',
  backgroundColor: COLORS.blue50,
  borderRadius: '20px',
}));
const StyledImageIcon = styled(ImageIcon)(() => ({
  width: '32px',
  height: '32px',
  path: {
    stroke: COLORS.primary,
  },
  circle: {
    stroke: COLORS.primary,
  },
}));

const StyledClickText = styled('span')(() => ({
  textDecoration: 'underline',
  color: COLORS.primary,
  marginRight: '4px',
}));
